// Persistent player state — survives full page reloads (iOS Safari memory
// eviction during long screen-locks, browser crash, manual refresh, etc.).
//
// Stored in localStorage so it works across tabs and across the kind of
// hard reloads iOS forces when it kills a backgrounded page. We deliberately
// avoid sessionStorage because Safari nukes that on tab termination.
//
// Shape:
//   { trackId: string, currentTime: number, closed: boolean, savedAt: number }
//
// closed=true is set when the user explicitly hits the X on the player. We
// honour that flag on the next page load so the player doesn't pop back up
// uninvited.

const KEY = 'cratelab_player_state';

export const readPlayerState = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.trackId !== 'string') return null;
    // Drop entries older than 24h — stops a stale track from suddenly
    // resurrecting itself days later.
    if (parsed.savedAt && Date.now() - parsed.savedAt > 24 * 60 * 60 * 1000) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const writePlayerState = (patch) => {
  try {
    const current = readPlayerState() || {};
    const merged = { ...current, ...patch, savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(merged));
  } catch {
    /* localStorage unavailable, silently ignore */
  }
};

export const clearPlayerState = () => {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
};

export const markPlayerClosed = () => {
  // Keep the trackId/currentTime around for analytics but flag it as closed
  // so we don't auto-restore until a new track is played.
  writePlayerState({ closed: true });
};
